import { Component, OnInit, inject } from "@angular/core";
import { FormControl, Validators, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { IRenameFolderPopUp } from "../models/rename-folder-popup.model";
import { IRenameFolderRequest } from "../models/rename-folder-request.model";
import { MessageService } from "../../../main";
import { AppSettingsService, SpinnerService, StudyCodeHelperService } from "../../../core";
import { BucketItemService } from "../service/bucket-item.service";
import { catchError, map, throwError } from "rxjs";
import { CommonConsants, FolderTypeConstants } from "../constants/component-constants";
import { IRenameFolderResponse } from "../models/rename-folder-response.model";
import { JustificationDialogComponent } from "../../../shared/justification-dialog/justification-dialog.component";
import { MatButton } from "@angular/material/button";
import { MatFormField, MatLabel, MatInput, MatError } from "@angular/material/input";

@Component({
    selector: "app-rename-popup",
    templateUrl: "./rename-popup.component.html",
    styleUrls: ["./rename-popup.component.scss"],
    imports: [MatButton, MatFormField, MatLabel, MatInput, FormsModule, ReactiveFormsModule, MatError]
})
export class RenamePopupComponent implements OnInit {
  private messageService = inject(MessageService);
  private appSettingService = inject(AppSettingsService);
  private bucketItemsService = inject(BucketItemService);
  private spinnerService = inject(SpinnerService);
  private dialog = inject(MatDialog);
  private studyCodeHelperService = inject(StudyCodeHelperService);
  private readonly dialogRef = inject<MatDialogRef<RenamePopupComponent>>(MatDialogRef);
  data = inject<IRenameFolderPopUp>(MAT_DIALOG_DATA);

  folderName = new FormControl("", [
    Validators.maxLength(CommonConsants.FILES_AND_FOLDERS_MAX_LENGTH),
    Validators.required,
    Validators.pattern('^[^/:*?\\\\"<>|]+$'),
  ]);
  public renameFolderPopup: IRenameFolderPopUp;
  public renameFolderRequest: IRenameFolderRequest;

  ngOnInit(): void {
    this.folderName.setValue(this.data.SelectedBucketItems[0].LeafName);

    this.folderName.valueChanges.subscribe((name) => {
      this.folderName.markAsTouched();
      if (
        this.folderName.value?.length > 0 &&
        this.data.SelectedBucketItems[0].FolderType ===
          FolderTypeConstants.STUDY_PACKAGE &&
        !this.studyCodeHelperService.isValidStudyCode(name ?? '')
      ) {
        this.folderName.setErrors({
          inValidStudyPackageFolderName: true,
        });
      } else if (
        this.folderName.value?.length > 0 &&
        this.data.SelectedBucketItems[0].FolderType ===
          FolderTypeConstants.FACILITY_PACKAGE &&
        this.studyCodeHelperService.isValidStudyCode(name ?? '')
      ) {
        this.folderName.setErrors({
          inValidFacilityPackageFolderName: true,
        });
      }
    });
  }

  /* #region Events and Methods */
  /** This method is to close the pop up*/
  public dismiss(): void {
    this.dialogRef.close(false);
  }

  /** This method is to close the pop up and to pass confirmation action */
  public renameFolder(): void {
    this.folderName.markAsTouched();

    if (this.folderName.valid && this.data.SelectedBucketItems.length === 1) {
      this.renameFolderRequest = {...this.renameFolderRequest,
        BucketItemId: this.data.SelectedBucketItems[0].Id,
        FolderName: this.folderName.value.trim(),
        BUCode: this.data.CurrentBreadcrumbItem.BUCode,
        BucketType: this.data.CurrentBreadcrumbItem.BucketType,
      };
      this.spinnerService.show(true);

      this.bucketItemsService
        .RenameFolder(this.renameFolderRequest)
        .pipe(
          map((res: IRenameFolderResponse) => {
            this.spinnerService.show(false);
            if(res.JustificationRequired){
              this.dialog.open(JustificationDialogComponent,{
                width:"450px",
                disableClose:true
              }).afterClosed().subscribe(
                (response: {comment:string})=>{
                  if(response && response.comment){
                    this.renameFolderRequest.Comment = response.comment;
                    //It will make the process continue after giving the justification
                    this.renameFolder();
                  }
                }
              );
            }
            else {
              this.messageService.success(
                "Folder name successfully updated",
                this.appSettingService.toastrDisplayTime
              );
              this.dialogRef.close(true);
            }
          }),
          catchError((error: any) => {
            this.dialogRef.close(false);
            this.spinnerService.show(false);
            if (error.error.detail !== undefined) {
              this.messageService.alert(
                error.error.detail,
                this.appSettingService.toastrDisplayTime
              );
            } else {
              this.messageService.alert(
                "Rename failed, please try again",
                this.appSettingService.toastrDisplayTime
              );
            }
            return throwError(() => {
              new Error(error.message);
            });
          })
        )
        .subscribe();
    }
  }
  /* #endregion */

  /** This method is to close the pop up and to pass confirmation action */
  public cancelAction(): void {
    this.dialogRef.close(false);
  }
  /* #endregion */
}
